import React from 'react';
import { CustomStepProps } from '../../types/survey';

export const PhysicalActivityAssessmentResults: React.FC<CustomStepProps> = ({
  onNext,
  onBack,
  surveyData,
}) => {
  // Calculate physical activity assessment score based on user's answers
  const calculatePhysicalActivityScore = () => {
    let totalScore = 0;
    const answers = surveyData?.answers || {};

    // Regular physical activity engagement scoring
    const physicalActivity = answers.physical_activity;
    if (physicalActivity) totalScore += physicalActivity; // 1-5 direct value

    // Physical activity frequency scoring
    const activityFrequency = answers.physical_activity_frequency;
    if (activityFrequency) totalScore += activityFrequency; // 1-5 direct value

    // Moderate activity duration scoring
    const moderateActivity = answers.moderate_activity;
    if (moderateActivity === 1) totalScore += 1; // <10 min
    else if (moderateActivity === 2) totalScore += 2; // 20 min
    else if (moderateActivity === 3) totalScore += 3; // 30 min
    else if (moderateActivity === 4) totalScore += 4; // 40 min
    else if (moderateActivity === 5) totalScore += 5; // 50+ min

    // Intense activity duration scoring
    const intenseActivity = answers.intense_activity;
    if (intenseActivity === 1) totalScore += 1; // <10 min
    else if (intenseActivity === 2) totalScore += 2; // 20 min
    else if (intenseActivity === 3) totalScore += 3; // 30 min
    else if (intenseActivity === 4) totalScore += 4; // 40 min
    else if (intenseActivity === 5) totalScore += 5; // 50+ min

    // Physical activity level self-rating (star rating, directly use the value)
    const activityLevel = answers.physical_activity_level;
    if (activityLevel) totalScore += activityLevel;

    return totalScore;
  };

  const score = calculatePhysicalActivityScore();
  const needsImprovement = score < 14;

  // Get activity level description
  const getActivityLevelText = () => {
    if (score < 7) return "Sedentary";
    if (score < 14) return "Low Activity";
    if (score < 20) return "Moderate Activity";
    return "High Activity";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-royal-blue-lightest via-white to-royal-gold-lightest flex items-center justify-center p-4">
      <div className="max-w-3xl w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section with Gradient */}
          <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/30 rounded-full mb-4">
                <svg className="w-10 h-10 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold mb-4 text-gray-800">
                Physical Activity Assessment
              </h1>
              <p className="text-xl text-gray-700">
                Your personalized exercise and movement analysis
              </p>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            {/* Score Display */}
            <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest rounded-2xl p-6 mb-8">
              <div className="text-center">
                <div className="text-5xl font-bold mb-2 text-gray-800">{score}</div>
                <div className="text-lg text-gray-700">Total Activity Score</div>
                <div className="text-sm text-gray-600 mt-2">{getActivityLevelText()}</div>
              </div>
            </div>

            {/* Summary Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Your Activity Profile
              </h2>
              <div className={`${needsImprovement ? 'bg-amber-50' : 'bg-green-50'} rounded-lg p-6`}>
                <h3 className="text-lg font-semibold mb-3 ${needsImprovement ? 'text-amber-900' : 'text-green-900'}">
                  {needsImprovement ? 'Time for a walk!' : 'Great job staying active!'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {needsImprovement ? (
                    <>
                      Every day we should spend at least 30 minutes engaging in physical activity. 
                      Even moderate, but regular physical activity has great benefits for the body. 
                      It helps reduce excess pounds or maintain a proper body weight, strengthens muscles, 
                      improves immunity, etc. Even the simplest activities such as walking, stretching, 
                      or dancing will be very beneficial for your health.
                    </>
                  ) : (
                    <>
                      Excellent work maintaining an active lifestyle! Regular physical activity is one of the most 
                      important things you can do for your health. You're reaping benefits including better weight control, 
                      reduced risk of chronic diseases, improved mental health and mood, stronger bones and muscles, 
                      and increased longevity. Keep up the fantastic work!
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Benefits of Physical Activity */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Benefits of Regular Exercise
              </h2>
              <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest rounded-lg p-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <span className="text-royal-gold mr-3 text-xl">💪</span>
                    <div>
                      <h3 className="font-semibold text-gray-800">Physical Health</h3>
                      <p className="text-sm text-gray-600">Stronger muscles, better cardiovascular health, improved immunity</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-royal-gold mr-3 text-xl">🧠</span>
                    <div>
                      <h3 className="font-semibold text-gray-800">Mental Health</h3>
                      <p className="text-sm text-gray-600">Reduced anxiety, better mood, improved cognitive function</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-royal-gold mr-3 text-xl">⚡</span>
                    <div>
                      <h3 className="font-semibold text-gray-800">Energy Levels</h3>
                      <p className="text-sm text-gray-600">Increased stamina, better sleep, reduced fatigue</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-royal-gold mr-3 text-xl">🎯</span>
                    <div>
                      <h3 className="font-semibold text-gray-800">Longevity</h3>
                      <p className="text-sm text-gray-600">Reduced risk of chronic diseases, healthier aging</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recommendations */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Our Experts Recommend
              </h2>
              <div className="space-y-3">
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-gold mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">150 minutes</span> of moderate-intensity aerobic activity per week
                    </p>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-blue mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">75 minutes</span> of vigorous-intensity aerobic activity per week
                    </p>
                  </div>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-gold mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">2+ days</span> of muscle-strengthening activities per week
                    </p>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-blue mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Break up</span> long periods of sitting with light activity
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Activity Types Grid */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Types of Physical Activity</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Aerobic Exercise</span>
                      <p className="text-xs text-gray-600 mt-1">Running, cycling, swimming</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Strength Training</span>
                      <p className="text-xs text-gray-600 mt-1">Weights, resistance bands</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Flexibility</span>
                      <p className="text-xs text-gray-600 mt-1">Yoga, stretching, pilates</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Balance</span>
                      <p className="text-xs text-gray-600 mt-1">Tai chi, stability exercises</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8">
              <button
                onClick={onBack}
                className="px-8 py-3 bg-white border-2 border-royal-gold text-royal-gold font-semibold rounded-lg hover:bg-royal-gold hover:text-white transition-colors"
              >
                Previous
              </button>
              <button
                onClick={onNext}
                className="px-8 py-3 bg-royal-gold text-white font-semibold rounded-lg hover:bg-royal-gold-dark transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};